<script setup>
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useUsersStore } from '@/stores'
import { useMessagingStore } from '@/stores/messaging.store'
import { useSupportStore } from '@/stores/support.store'
import { useRouter } from 'vue-router'
import { exitSimulation, simulation } from "@/plugins/misc"
import { useDesignSystem } from '@/composables/useDesignSystem'
import appStoreImage from "@/assets/app-store.svg"
import CmsText from "@/@core/components/CmsText.vue"
import { isRestrictedSimulationScope } from '@/utils/simulationScopes'
import { rmViaTextVisible } from '@/utils/rmViaText'
import axios from '@axios'

const isUserLoggedIn = localStorage.getItem('userData') ? true : false
const userLoggedInAndNotDummy = isUserLoggedIn && JSON.parse(localStorage.getItem('userData')).email.includes('@')

const usersStore = useUsersStore()
let myUser = null
let isRunningInIOSAppStore = null
let loginDialogMode = null
let canActForMembers = null
  ; ({ myUser, isRunningInIOSAppStore, loginDialogMode, canActForMembers } = storeToRefs(usersStore))

// RM-159: the acting-as switcher may only list members the actor can actually
// simulate. Tier 1 spouses (canSimulate === false) can submit reservations but
// not "Manage Account", so they're excluded here while remaining in the
// reservation host picker.
const simulatableMembers = computed(() => (canActForMembers.value || [])
  .filter(m => m.canSimulate !== false))

// RM-159: surface the spouse/partner as a dedicated "Manage <name>'s Account"
// tile, separate from the generic group "Manage Accounts" picker. Without this, a
// member who is ALSO a corporate-group admin has their spouse buried among many
// group members (e.g. Justin → 185 Shore Capital members + Kristen), unlike a
// non-admin spouse who, having exactly one simulatable member, sees the named
// tile directly. The dedicated tile gives both directions identical one-tap access.
const simulatableSpouse = computed(() => (canActForMembers.value || [])
  .find(m => m.relationship === 'spouse' && m.canSimulate !== false) || null)

// The generic switcher lists everyone EXCEPT the spouse (they have their own tile).
const manageableMembers = computed(() => (simulatableMembers.value || [])
  .filter(m => m.relationship !== 'spouse'))

const isSimulation = !!localStorage.getItem('simulation')

// RM-159: distinguish a RESTRICTED simulation (a personal admin, spouse or — since
// RM-319 — group admin acting for a member; the backend blocks billing/membership/
// deletion for these via RESTRICTED_SIMULATION_SCOPES) from a platform (RM) admin
// simulation, which the backend fully privileges. Controls RM-150 hid during *any*
// simulation are hidden only for restricted scopes, so the RM admin sees the
// member's full view. The one client list: utils/simulationScopes.
const simulationScope = localStorage.getItem('simulationScope')
const isRestrictedSimulation = isSimulation && isRestrictedSimulationScope(simulationScope)

// RM-159: show the Spouse/Partner Account tile during simulation whenever the
// simulated account actually has a linked spouse to display. This is data-driven
// rather than scope-driven on purpose: classifyActorRelationship returns the
// HIGHEST-priority role, so a platform admin who is also the member's spouse
// (e.g. Kapil → Cailie) simulates as 'platform-admin', and a pure scope check
// would wrongly hide the tile. Keying off the link itself covers both a spouse
// using Tier 2 to manage their partner and an admin viewing a spouse-linked
// member, while members WITHOUT a spouse keep a clean menu during simulation.
const hasSpouseLink = computed(() => !!(myUser.value?.primaryAccount || myUser.value?.secondaryAccount))

const router = useRouter()

// Messaging unread badge
const messagingStore = useMessagingStore()
const { totalUnread } = storeToRefs(messagingStore)

// Support unread badge
const supportStore = useSupportStore()
const { memberUnreadCount: supportUnreadCount } = storeToRefs(supportStore)

if (userLoggedInAndNotDummy) {
  messagingStore.fetchMemberUnreadCount()
  supportStore.fetchMemberUnreadCount()

  // Fetch for everyone, including platform admins: the endpoint now returns an
  // admin's personal Platinum spouse (RM-159), which drives the dedicated
  // "Manage <spouse>'s Account" tile. For a non-spouse admin it returns [] and
  // the generic switcher stays hidden (they use the admin search instead).
  usersStore.fetchCanActForMembers()
}

// RM-430: the RM via text tile (Kapil 10/24: its own tile on the Profile). It
// shows when texting is on, or when the member can turn it on in the app
// (utils/rmViaText). Not while someone manages the account for the member:
// only the member can consent for their own phone.
const rmViaText = ref(null)
const showRmViaText = computed(() => rmViaTextVisible(rmViaText.value))
if (userLoggedInAndNotDummy && !isRestrictedSimulation) {
  axios.get('/agent/sms/me')
    .then(({ data }) => { rmViaText.value = data })
    .catch(() => { rmViaText.value = null })
}

const startActingAs = email => {
  simulation(email)
}

// RM-150: account-switcher consolidation. Instead of rendering one menu tile
// per member (which floods the grid when an actor admins for many people), a
// single "Manage Accounts" tile opens a picker dialog. With exactly one member
// we skip the dialog and act directly — no redundant step.
const showManageAccountsDialog = ref(false)

const openManageAccounts = () => {
  const members = manageableMembers.value || []
  if (members.length === 1) {
    startActingAs(members[0].email)

    return
  }
  showManageAccountsDialog.value = true
}

const selectMemberToManage = email => {
  showManageAccountsDialog.value = false
  startActingAs(email)
}

const memberInitials = member => {
  const first = (member?.firstName || '').trim()
  const last = (member?.lastName || '').trim()
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.charAt(0).toUpperCase()
  }

  return (member?.email || '?').charAt(0).toUpperCase()
}

const memberDisplayName = member => {
  const name = `${member?.firstName || ''} ${member?.lastName || ''}`.trim()

  return name || member?.email || 'Member'
}

async function openSupportThread() {
  try {
    const conv = await supportStore.getOrCreateSupportConversation()
    if (conv?._id) {
      router.push({ path: '/messages', query: { conv: conv._id } })
    }
  } catch (err) {
    console.error('Failed to open support thread', err)
  }
}

const { dialogWidth, smAndDown } = useDesignSystem()
const nominateDialogWidth = dialogWidth('sm')

const showNominateFriendsDialog = ref(false)
const isCopied = ref(false)

const logout = () => {
  localStorage.removeItem('userData')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('jwt')
  localStorage.removeItem('simulation')
  localStorage.removeItem('simulationStack')
  localStorage.removeItem('simulationHref')
  localStorage.removeItem('simulationScope')
  localStorage.removeItem('adminJwt')
  location.href = "/auth/logout?loc=3"
}

// Referral link for nominate friends
const referralLink = computed(() => {
  if (myUser.value?.profileId) {
    return `reserverm.com/${myUser.value.profileId}/friends`
  }

  return ''
})

const { t } = useI18n()

const referralMessage = computed(() => {
  if (!referralLink.value) return ''

  return `${t('menu.referral.referralMessage')} https://${referralLink.value}`
})

const copyReferralMessage = () => {
  if (referralMessage.value) {
    navigator.clipboard.writeText(referralMessage.value)
    isCopied.value = true
    setTimeout(() => {
      isCopied.value = false
    }, 2000)
  }
}
</script>

<template>
  <div class="profile-menu-section">
    <!-- Not logged in - show login/join buttons -->
    <template v-if="!userLoggedInAndNotDummy">
      <div class="menu-auth-buttons">
        <VBtn
          variant="flat"
          size="small"
          color="primary"
          class="auth-btn"
          @click="loginDialogMode = 'activate'"
        >
          <CmsText k="menu.requestToJoin" />
        </VBtn>
        <VBtn
          variant="flat"
          size="small"
          color="#efefef"
          class="auth-btn"
          @click="loginDialogMode = 'login'"
        >
          <CmsText k="menu.logIn" />
        </VBtn>
      </div>
    </template>

    <!-- Icon menu grid (logged in) -->
    <template v-if="userLoggedInAndNotDummy">
      <div class="icon-menu-grid">
        <!-- Admin Account (RM-150) — top of grid, before Connect with Friends -->
        <div
          v-if="myUser?.activeSubscription && !myUser?.isSecondaryAccount && myUser?.isPlatinum"
          class="icon-menu-item"
          @click="usersStore.showPersonalAdminsDialog()"
        >
          <div class="icon-menu-circle">
            <VIcon size="22">
              mdi-account-key-outline
            </VIcon>
          </div>
          <span class="icon-menu-label">Admin Account <span class="beta-tag"><br>Beta</span></span>
        </div>

        <!-- Discover Friends' Favorites -->
        <RouterLink
          to="/network"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle
                cx="9"
                cy="7"
                r="4"
              /><line
                x1="19"
                x2="19"
                y1="8"
                y2="14"
              /><line
                x1="22"
                x2="16"
                y1="11"
                y2="11"
              />
            </svg>
          </div>
          <CmsText
            k="menu.discoverFriendsFavorites"
            class="icon-menu-label"
          />
        </RouterLink>

        <!-- FAQs -->
        <RouterLink
          to="/members"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
              /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
            </svg>
          </div>
          <CmsText
            k="menu.faqs"
            class="icon-menu-label"
          />
        </RouterLink>

        <!-- Give Friends Platinum (platinum only) -->
        <div
          v-if="myUser?.isPlatinum"
          class="icon-menu-item"
          @click="showNominateFriendsDialog = true"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 12v10H4V12" /><path d="M2 7h20v5H2z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
            </svg>
          </div>
          <CmsText
            k="menu.giveFriendsPlatinumAccess"
            class="icon-menu-label"
          />
        </div>

        <!-- Manage Group Members (group admin/owner only, non-restaurant) -->
        <RouterLink
          v-if="myUser?.primaryGroupType !== 'restaurant' && (myUser?.primaryGroupPermission === 'owner' || myUser?.primaryGroupPermission === 'admin' || myUser?.primaryGroupPermission === 'adminStandard')"
          to="/group-members"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 21a8 8 0 0 0-16 0" /><circle
                cx="10"
                cy="8"
                r="5"
              /><path d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />
            </svg>
          </div>
          <CmsText
            k="menu.members"
            class="icon-menu-label"
          />
        </RouterLink>

        <!--
          Membership — hidden only for RESTRICTED simulation scopes (personal-admin
          / spouse / group-admin), who can't manage billing. The platform (RM) admin sees it so
          their simulation matches the member's full view (RM-150 / RM-159). 
        -->
        <RouterLink
          v-if="!isRestrictedSimulation"
          to="/membership"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                width="20"
                height="14"
                x="2"
                y="5"
                rx="2"
              /><line
                x1="2"
                x2="22"
                y1="10"
                y2="10"
              />
            </svg>
          </div>
          <CmsText
            k="menu.membershipDetails"
            class="icon-menu-label"
          />
        </RouterLink>

        <!-- Messages -->
        <RouterLink
          to="/messages"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle icon-menu-circle--badge-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span
              v-if="totalUnread > 0"
              class="unread-badge"
            >{{ totalUnread > 99 ? '99+' : totalUnread }}</span>
          </div>
          <span class="icon-menu-label">Messages</span>
        </RouterLink>

        <!--
          Notes — the member's general Dining Profile notes (RM-294 entries with no
          restaurant). They used to sit permanently under the Profile card; founder
          review 2026-09-18 made them a destination like every other tile here.
          Hidden during a RESTRICTED simulation (personal-admin / spouse / group-admin) because the
          notes are owner-only: the service refuses those callers, exactly as the
          Membership tile is hidden from them.
        -->
        <RouterLink
          v-if="!isRestrictedSimulation"
          to="/notes"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M8 2v4" /><path d="M12 2v4" /><path d="M16 2v4" /><rect
                width="16"
                height="18"
                x="4"
                y="4"
                rx="2"
              /><path d="M8 10h6" /><path d="M8 14h8" /><path d="M8 18h5" />
            </svg>
          </div>
          <span class="icon-menu-label">Notes</span>
        </RouterLink>

        <!-- Partners -->
        <RouterLink
          to="/luxurypartners"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20.42 4.58a5.4 5.4 0 0 0-7.65 0L12 5.35l-.77-.77a5.4 5.4 0 0 0-7.65 7.65l8.42 8.42 8.42-8.42a5.4 5.4 0 0 0 0-7.65z" />
            </svg>
          </div>
          <CmsText
            k="menu.partners"
            class="icon-menu-label"
          />
        </RouterLink>

        <!-- Restaurant Admin -->
        <!--  || (myUser?.primaryGroupType == 'restaurant' && (myUser?.primaryGroupPermission == 'owner' || myUser?.primaryGroupPermission == 'admin' || myUser?.primaryGroupPermission == 'adminStandard')) -->
        <RouterLink
          v-if="myUser?.userLevel == 'admin'"
          to="/admin/profile"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2v0a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
            </svg>
          </div>
          <span class="icon-menu-label">Restaurant Admin</span>
        </RouterLink>

        <!-- Restaurants -->
        <RouterLink
          to="/restaurants"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <VIcon size="22">
              mdi-storefront-outline
            </VIcon>
          </div>
          <CmsText
            k="menu.restaurants"
            class="icon-menu-label"
          />
        </RouterLink>

        <!-- RM via text (RM-430): turn it on, or see it's on and RM's number -->
        <RouterLink
          v-if="showRmViaText"
          to="/rm-via-text"
          class="icon-menu-item"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                width="14"
                height="20"
                x="5"
                y="2"
                rx="2"
                ry="2"
              /><path d="M12 18h.01" />
            </svg>
          </div>
          <span class="icon-menu-label">RM via text</span>
        </RouterLink>

        <!--
          Spouse/Partner Account — always shown for the real account holder and for a
          platform (RM) admin simulation so the member's full view is preserved (set up
          or view). During a RESTRICTED simulation (personal-admin / spouse / group-admin) it shows
          only when the simulated account already has a linked spouse to view
          (RM-150 hid it outright; RM-159 reveals the existing link). 
        -->
        <div
          v-if="!isRestrictedSimulation || hasSpouseLink"
          class="icon-menu-item"
          @click="usersStore.showSecondaryMemberDialog()"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle
                cx="9"
                cy="7"
                r="4"
              /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <CmsText
            k="menu.secondaryAccount"
            class="icon-menu-label"
          />
        </div>

        <!--
          RM-159: dedicated "Manage <spouse>'s Account" tile. Always shown when the
          actor has a Platinum spouse who has opted in to account access, so the
          spouse relationship is one tap away in BOTH directions — independent of
          how many group members the actor administers. (A group-admin primary
          would otherwise have their spouse buried in the generic picker below.)
        -->
        <div
          v-if="simulatableSpouse && !isRestrictedSimulation"
          class="icon-menu-item"
          role="button"
          tabindex="0"
          @click="startActingAs(simulatableSpouse.email)"
          @keydown.enter.prevent="startActingAs(simulatableSpouse.email)"
          @keydown.space.prevent="startActingAs(simulatableSpouse.email)"
        >
          <div class="icon-menu-circle">
            <VIcon size="22">
              mdi-account-heart-outline
            </VIcon>
          </div>
          <span class="icon-menu-label">
            Manage {{ simulatableSpouse.firstName || 'Spouse' }}’s Account
          </span>
        </div>

        <!--
          RM-150: Acting-as switcher — a single consolidated tile for the actor's
          group/personal-admin members (the spouse is excluded; it has its own tile
          above). Shows when the actor is a group admin or active personal admin for
          one or more members. `canActForMembers` is only fetched for
          non-platform-admin users, so a real platform admin's own menu never shows
          it (they use the AdminDirectReservationDialog autocomplete instead). It IS
          shown during a simulation session, however: a simulated user should keep
          every capability the real user has, including switching to manage their
          own members. Clicking opens a member picker (or acts directly when there
          is exactly one member).
        -->
        <div
          v-if="manageableMembers && manageableMembers.length > 0 && !isRestrictedSimulation"
          class="icon-menu-item"
          role="button"
          tabindex="0"
          @click="openManageAccounts"
          @keydown.enter.prevent="openManageAccounts"
          @keydown.space.prevent="openManageAccounts"
        >
          <div class="icon-menu-circle icon-menu-circle--badge-wrapper">
            <VIcon size="22">
              mdi-account-switch-outline
            </VIcon>
            <span
              v-if="manageableMembers.length > 1"
              class="count-badge"
            >{{ manageableMembers.length }}</span>
          </div>
          <span class="icon-menu-label">
            {{ manageableMembers.length === 1 ? `Manage ${manageableMembers[0].firstName || 'Member'}’s Account` : 'Manage Accounts' }}
          </span>
        </div>

        <!-- Support -->
        <div
          class="icon-menu-item"
          @click="openSupportThread"
        >
          <div class="icon-menu-circle icon-menu-circle--badge-wrapper">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M12 7v2" /><path d="M12 13h.01" />
            </svg>
            <span
              v-if="supportUnreadCount > 0"
              class="unread-badge"
            >{{ supportUnreadCount > 99 ? '99+' : supportUnreadCount }}</span>
          </div>
          <span class="icon-menu-label">Support</span>
        </div>

        <!-- Logout -->
        <div
          class="icon-menu-item"
          @click="logout"
        >
          <div class="icon-menu-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line
                x1="21"
                x2="9"
                y1="12"
                y2="12"
              />
            </svg>
          </div>
          <CmsText
            k="menu.logout"
            class="icon-menu-label"
          />
        </div>

        <!-- 9. Exit Simulation -->
        <div
          v-if="isSimulation"
          class="icon-menu-item"
          @click="exitSimulation"
        >
          <div class="icon-menu-circle icon-menu-circle--warning">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M13 4h3a2 2 0 0 1 2 2v14" /><path d="M2 20h3" /><path d="M13 20h9" /><path d="M10 12v.01" /><path d="M13 4.562v16.157a1 1 0 0 1-1.242.97L5 20V5.562a2 2 0 0 1 1.515-1.94l4-1A2 2 0 0 1 13 4.561Z" />
            </svg>
          </div>
          <CmsText
            k="menu.exitSimulation"
            class="icon-menu-label"
          />
        </div>
      </div>
    </template>

    <!-- Nominate Friends dialog (platinum only) -->
    <VDialog
      v-if="userLoggedInAndNotDummy && myUser?.isPlatinum"
      v-model="showNominateFriendsDialog"
      :width="nominateDialogWidth"
      :fullscreen="smAndDown"
    >
      <VCard class="nominate-friends-card">
        <div class="nominate-friends-header">
          <CmsText
            k="menu.giveFriendsPlatinumAccess"
            tag="h2"
            class="nominate-friends-title"
          />
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showNominateFriendsDialog = false"
          />
        </div>

        <div class="nominate-friends-body">
          <CmsText
            k="menu.referral.intro"
            tag="p"
            class="nominate-friends-text"
          />

          <div class="referral-message-text">
            <CmsText k="menu.referral.referralMessage" />
            <RouterLink
              :to="`/${myUser?.profileId}/friends`"
              class="referral-link-inline"
            >
              {{ referralLink }}
            </RouterLink>
          </div>

          <VBtn
            block
            size="large"
            :color="isCopied ? 'success' : 'primary'"
            class="copy-share-btn"
            @click="copyReferralMessage"
          >
            <template #prepend>
              <svg
                v-if="!isCopied"
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect
                  width="14"
                  height="14"
                  x="8"
                  y="8"
                  rx="2"
                  ry="2"
                />
                <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </template>
            <CmsText
              v-if="isCopied"
              k="menu.referral.copied"
            />
            <CmsText
              v-else
              k="menu.referral.copyShare"
            />
          </VBtn>

          <CmsText
            k="menu.referral.faq"
            tag="p"
            class="nominate-friends-subtext"
          />
        </div>
      </VCard>
    </VDialog>

    <!-- RM-150: Manage Accounts picker (group/personal admins) -->
    <VDialog
      v-if="userLoggedInAndNotDummy"
      v-model="showManageAccountsDialog"
      :width="nominateDialogWidth"
      :fullscreen="smAndDown"
    >
      <VCard class="manage-accounts-card">
        <div class="manage-accounts-header">
          <div>
            <h2 class="manage-accounts-title">
              Manage Accounts
            </h2>
            <p class="manage-accounts-subtitle">
              Choose whose account to manage
            </p>
          </div>
          <VBtn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showManageAccountsDialog = false"
          />
        </div>

        <div class="manage-accounts-list">
          <button
            v-for="m in manageableMembers"
            :key="m.email"
            type="button"
            class="manage-account-row"
            @click="selectMemberToManage(m.email)"
          >
            <span class="manage-account-avatar">{{ memberInitials(m) }}</span>
            <span class="manage-account-info">
              <span class="manage-account-name">{{ memberDisplayName(m) }}</span>
              <span class="manage-account-email">{{ m.email }}</span>
            </span>
            <VIcon
              size="20"
              class="manage-account-chevron"
            >
              mdi-chevron-right
            </VIcon>
          </button>
        </div>
      </VCard>
    </VDialog>

    <div
      v-if="!isRunningInIOSAppStore"
      class="app-store-badge"
    >
      <a
        href="https://apps.apple.com/us/app/rm-preferred-reservations/id6475874605"
        target="_blank"
        rel="noopener noreferrer"
        style="display: inline-block;"
      >
        <img
          alt="Download on the App Store"
          :src="appStoreImage"
          style="height: 36px;"
        >
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.profile-menu-section {
  align-self: stretch;
  width: 100%;
}

.menu-auth-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--ds-space-5) var(--ds-space-4);
  gap: var(--ds-space-3);
}

.icon-menu-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--ds-space-1) 0;
  padding: var(--ds-space-4) var(--ds-space-2) var(--ds-space-3);
}

.icon-menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ds-space-2);
  padding: var(--ds-space-3) var(--ds-space-2);
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: background-color var(--ds-duration-normal);

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:hover .icon-menu-circle {
    border-color: var(--ds-brand-primary);
    color: var(--ds-brand-primary);
  }
}

.icon-menu-circle {
  width: 52px;
  height: 52px;
  border-radius: var(--ds-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgb(248, 249, 250);
  border: 1.5px solid var(--ds-border-default);
  color: var(--ds-text-secondary);
  transition: all var(--ds-duration-normal);

  &.active {
    background-color: var(--ds-brand-primary-light, #e9f4f3);
    border-color: var(--ds-brand-primary, #4db6ac);
    color: var(--ds-brand-primary-dark, #3F998F);
  }

  &--warning {
    color: rgb(198, 130, 60);
    border-color: rgb(230, 185, 130);
    background-color: rgb(255, 248, 240);
  }

  &--badge-wrapper {
    position: relative;
  }
}

.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--ds-radius-sm);
  background-color: #e53935;
  color: var(--ds-text-on-dark);
  font-size: var(--ds-text-xs);
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: var(--ds-radius-full);
  background-color: var(--ds-brand-primary, #4db6ac);
  color: var(--ds-text-on-dark);
  font-size: var(--ds-text-xs);
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.icon-menu-label {
  font-size: var(--ds-text-xs);
  font-weight: 500;
  color: var(--ds-text-secondary);
  text-align: center;
  line-height: 1.3;
  max-width: 90px;
}

.manage-accounts-card {
  padding: 0;
}

.manage-accounts-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--ds-space-3);
  padding: var(--ds-space-4) var(--ds-space-5);
  border-bottom: 1px solid var(--ds-border-light);
}

.manage-accounts-title {
  margin: 0;
  font-size: var(--ds-text-xl);
  font-weight: 600;
  color: var(--ds-text-strong);
  line-height: 1.3;
}

.manage-accounts-subtitle {
  margin: var(--ds-space-1) 0 0;
  font-size: var(--ds-text-sm);
  color: var(--ds-text-muted);
}

.manage-accounts-list {
  display: flex;
  flex-direction: column;
  padding: var(--ds-space-2) var(--ds-space-3) var(--ds-space-4);
}

.manage-account-row {
  display: flex;
  align-items: center;
  gap: var(--ds-space-3);
  width: 100%;
  padding: var(--ds-space-3);
  border: none;
  border-radius: var(--ds-radius-md);
  background-color: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color var(--ds-duration-normal);

  &:hover,
  &:focus-visible {
    background-color: rgba(0, 0, 0, 0.04);
  }

  &:focus-visible {
    outline: 2px solid var(--ds-brand-primary, #4db6ac);
    outline-offset: -2px;
  }
}

.manage-account-avatar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--ds-radius-full);
  background-color: var(--ds-brand-primary-light, #e9f4f3);
  color: var(--ds-brand-primary-dark, #3F998F);
  font-size: 14px;
  font-weight: 600;
}

.manage-account-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}

.manage-account-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-strong);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.manage-account-email {
  font-size: var(--ds-text-sm);
  color: var(--ds-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.manage-account-chevron {
  flex: 0 0 auto;
  color: var(--ds-text-muted);
}

.beta-tag {
  font-weight: 400;
  opacity: 0.7;
  color: var(--ds-brand-primary-darker);
}

.nominate-friends-card {
  padding: 0;
}

.nominate-friends-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3);
  padding: var(--ds-space-4) var(--ds-space-5);
  border-bottom: 1px solid var(--ds-border-light);
}

.nominate-friends-title {
  margin: 0;
  font-size: var(--ds-text-xl);
  font-weight: 600;
  color: var(--ds-text-strong);
  line-height: 1.3;
}

.nominate-friends-body {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
  padding: var(--ds-space-5);
}

.nominate-friends-text {
  margin: 0;
  font-size: 16px;
  line-height: 1.6;
  color: var(--ds-text-secondary);
}

.referral-message-text {
  padding: var(--ds-space-3);
  background-color: var(--ds-surface-subtle);
  border-radius: var(--ds-radius-sm);
  font-size: 16px;
  line-height: var(--ds-leading-normal);
  color: var(--ds-text-strong);
  border: 1px solid var(--ds-border-default);
}

.referral-link-inline {
  color: rgb(0, 102, 204);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.copy-share-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 16px;
  font-weight: 600;
}

.nominate-friends-subtext {
  margin: 0;
  font-size: var(--ds-text-sm);
  color: var(--ds-text-muted);
  line-height: 1.5;
}

.footer-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--ds-space-3);
  padding: var(--ds-space-2) var(--ds-space-4);
  margin-top: var(--ds-space-1);
}

.footer-link {
  font-size: var(--ds-text-sm);
  color: var(--ds-brand-primary-dark, #3F998F);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.footer-divider {
  width: 1px;
  height: var(--ds-space-3);
  background-color: rgb(210, 210, 210);
}

.app-store-badge {
  display: flex;
  justify-content: center;
  margin-top: var(--ds-space-3);
  margin-bottom: var(--ds-space-2);
}
</style>
